<template>
 <div>
   <p v-if="estimate.estimateType==='delete'">{{t('Just one more step! Confirm the switching off of add-on and get more details on the terms of your subscription and previously purchased add-ons. The remnant payment will be returned to your account balance.')}}</p>
   <p v-if="estimate.estimateType!=='delete'">{{t('Just one more step! Confirm the purchase and get more details on the terms of your subscription and purchased add-ons.')}}</p>
   <div class="row">
     <div class="cell"><b>{{planLimitsKeyText[data.planLimit]}}</b></div>
     <div class="cell">{{finalAmount}}</div>
   </div>
   <div class="row" v-if="creditNoteEstimates">
     <div class="cell">{{t('Returned amount')}}</div>
     <div class="cell">${{ numeral(creditNoteEstimates/ 100).format('0,0.00')}}</div>
   </div>
   <div class="row" v-if="estimate.estimateType!=='delete' && estimate.invoice_estimate">
     <div class="cell">{{ t('Prorated cost') }} ({{formatBillingDate(estimate.created_at)}} - {{formatBillingDate(nextBillingAt)}})</div>
     <div class="cell">${{ numeral(invoiceEstimate.total/ 100).format('0,0.00')}}</div>
   </div>
   <ui-info color="red" class="mb-8 mt-16" v-if="estimate.estimateType === 'lessThan50cents'">
     {{ t('Due to the limitation of billing system the payment less than $0.5 can not be accepted. For assistance contact the team via live chat.') }}
   </ui-info>
   <div class="row" v-if="estimate.invoice_estimate">
     <div class="cell">{{ t('Cost on renewal') }} ({{formatBillingDate(nextBillingAt)}} - {{formatBillingDate(nextBillingEnd)}})</div>
     <div class="cell">${{ numeral(pricePerMonth/ 100).format('0,0.00')}}</div>
   </div>
   <div class="row-title" v-if="prevPurchasedAddons.length">
     <div class="cell"><b>{{t('Previously purchased add-ons')}}</b></div>
     <div class="cell"></div>
   </div>
   <template v-for="addon in prevPurchasedAddons">
     <div class="row">
       <div class="cell">{{planLimitsKeyText[addon.addonName]}}</div>
       <div class="cell">${{ numeral(addon.addonTotalPrice/ 100).format('0,0.00')}}</div>
     </div>
   </template>
   <div class="row-title">
     <div class="cell"><b>{{t('Plan')}}:</b></div>
     <div class="cell"></div>
   </div>
   <div class="row">
     <div class="cell">{{ store.billing.planName }}</div>
     <div class="cell">${{ numeral(store.billing.planPrice/ 100).format('0,0.00')}}</div>
   </div>
   <div class="row">
     <div class="cell">
       <b v-html="t('Total cost (should be paid on {0})',[formatBillingDate(nextBillingAt)])"></b>
     </div>
     <div class="cell">${{numeral(totalShouldBePaidOn/ 100).format('0,0.00')}}</div>
   </div>
   <div class="row" v-if="estimate.invoice_estimate">
     <div class="cell"><b>{{t('Amount charged from the balance')}}</b></div>
     <div class="cell">${{numeral(creditApplied / 100).format('0,0.00')}}</div>
   </div>
   <div class="row" v-if="estimate.invoice_estimate">
     <div class="cell"><b>{{t('Total now')}}</b></div>
     <div class="cell">${{numeral(invoiceEstimate.amount_due/ 100).format('0,0.00')}}</div>
   </div>
   <p class="mt-16" v-html="t('If any assistance is required - contact the team via Live Chat.')"></p>
 </div>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';
import numeral from 'numeral';
import { useI18n, usePlanLimits } from '@/composables';
import {
  formatBillingDate,
  getNextBillingAt,
  getNextBillingEnd,
} from '@/services/utils/billing-format';
import { useStore } from '@/store';

type PlanAddon = {
  addonName: keyof BillingLimits;
  addonTotalPrice: number;
};

const props = withDefaults(
  defineProps<{
    estimate: any;
    data: {
      planLimit: null | keyof BillingLimits;
    };
    finalAmount?: number | null;
    pricePerMonth?: number;
  }>(),
  {
    finalAmount: null,
    pricePerMonth: 0,
  },
);

const { t } = useI18n();
const { planLimitsKeyText } = usePlanLimits(undefined);
const { estimate, data, finalAmount, pricePerMonth } = toRefs(props);

const store = useStore();
const nextBillingAt = computed(() => getNextBillingAt(store.billing));
const nextBillingEnd = computed(() => getNextBillingEnd(store.billing));

const invoiceEstimate = computed(
  () => props.estimate?.invoice_estimate || props.estimate?.next_invoice_estimate,
);

const creditNoteEstimates = computed(() => props.estimate?.credit_note_estimates?.[0]?.total);

const prevPurchasedAddons = computed(() => {
  const addonsArray = Object.values(store.billing.addons || {}) as PlanAddon[];
  return addonsArray.filter(item => item.addonName !== props.data.planLimit);
});

const totalShouldBePaidOn = computed(() => {
  const allOldAddonsPrice = prevPurchasedAddons.value.reduce((acc, addon) => {
    return acc + +addon.addonTotalPrice;
  }, 0);
  const oldPrice = store.billing.planPrice + allOldAddonsPrice;
  return props.estimate?.estimateType === 'delete' ? oldPrice : oldPrice + props.pricePerMonth;
});

const creditApplied = computed(() => {
  const discountsAmount =
    invoiceEstimate.value?.discounts?.reduce((acc: number, item: { amount: number }) => {
      return acc + item.amount;
    }, 0) || 0;
  return (invoiceEstimate.value?.credits_applied || 0) + discountsAmount;
});
</script>

<style lang="scss" scoped> 
.row{
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--color-gray10);
  &:last-child{
    border-bottom: none;
  }
}

.row-title{
  display: flex;
  justify-content: space-between;
  padding: 12px 0 0;
}

.cell{
  font-size: 14px;
}
</style>
